'use client';

import * as Dialog from '@radix-ui/react-dialog';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { type z } from 'zod';
import { object as __eliadmin_zod_0, string as __eliadmin_zod_1, number as __eliadmin_zod_2 } from 'zod';
import { ensureZodDefaults as __eliadmin_zod_defaults } from '@eliadmin/core/src/build/zod-defaults';
__eliadmin_zod_defaults();

import { CacheKey } from '@eliadmin/core/src/api/constants';
import { ValidationError } from '@eliadmin/core/src/api/errors';
import { createProductReview } from '@eliadmin/core/src/api/products/product-api';
import { DialogOverlay } from '@eliadmin/core/src/components/dlz/dialogs/dialog-overlay';
import {
  REVIEW_COPY,
  REVIEW_MAX_LENGTH,
} from '@eliadmin/core/src/components/dlz/product-detail/reviews/review-copy';
import { ReviewAddedDialog } from '@eliadmin/core/src/components/dlz/product-detail/reviews/review-added-dialog';
import { ReviewRatingInput } from '@eliadmin/core/src/components/dlz/product-detail/reviews/review-rating-input';
import { Icon } from '@eliadmin/core/src/components/dlz/ui/icon';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { useDialogStore } from '@eliadmin/core/src/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@eliadmin/core/src/components/ui/form';
import { useToast } from '@eliadmin/core/src/components/ui/use-toast';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import { normalizeIntlLocale } from '@eliadmin/core/src/utils/intl-utils';
import type { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';

/**
 * Same rules as the core schema. The rating message is the field's own hint: the core
 * `REVIEW_RATING_RANGE_VALIDATION_MESSAGE` resolves to `text744`, which is „Súvisiace produkty“.
 */
export const addReviewSchema = (VM: FormValidationMessages) =>
  __eliadmin_zod_0({
    review: __eliadmin_zod_1().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    rating: __eliadmin_zod_2()
      .int()
      .min(1, REVIEW_COPY.form.ratingHint)
      .max(5, REVIEW_COPY.form.ratingHint),
  });

type AddReviewValues = z.infer<ReturnType<typeof addReviewSchema>>;

export type AddReviewDialogProps = {
  slug: string;
};

/**
 * Core override – „Napísať hodnotenie“ (Figma 60152:14462, mobile 60459:106780): a card on desktop,
 * a bottom sheet on mobile. Only the fields the review API stores are built: rating and text.
 * Posting, the 422 handling and the cache refresh are the core flow; the success toast is replaced
 * by the „Hodnotenie pridané“ dialog.
 */
export function AddReviewDialog(props: AddReviewDialogProps) {
  const close = useDialogStore(s => s.close);
  const openDialog = useDialogStore(s => s.open);
  const { t } = useTranslations();

  const me = useCustomerStore(s => s.me);
  const isInitialized = useCustomerStore(s => s.isInitialized);
  const locale = useLocaleStore(s => s.currentLocale);
  const productName = useVariantsStore(s => s.product.name);
  const router = useRouter();
  const { toast } = useToast();

  const queryClient = useQueryClient();
  const createReviewMutation = useMutation({ mutationFn: createProductReview });

  const { formValidationMessages } = useFormValidationMessages(t);

  const form = useForm<AddReviewValues>({
    defaultValues: {
      rating: 0,
      review: '',
    },
    resolver: zodResolver(addReviewSchema(formValidationMessages)),
  });

  const handleSubmit = form.handleSubmit(async data => {
    if (!me?.authToken) return;

    try {
      await createReviewMutation.mutateAsync({
        authToken: me.authToken,
        body: data,
        locale,
        slug: props.slug,
      });
      router.refresh();
      close(AddReviewDialog.id);
      openDialog({ id: ReviewAddedDialog.id, content: <ReviewAddedDialog /> });
      queryClient.invalidateQueries({ queryKey: [CacheKey.PRODUCTS_REVIEWS] });
    } catch (e) {
      // handle 422
      if (e instanceof ValidationError) {
        const unknown: string[] = [];
        for (const [field, errors] of Object.entries(e.fields)) {
          if (field === 'review' || field === 'rating') {
            form.setError(field, { type: 'manual', message: errors.join('\n') });
          } else {
            unknown.push(...errors);
          }
        }
        if (unknown.length) {
          toast({ title: t('text661'), description: unknown.join('\n'), variant: 'destructive' });
        }
        return;
      }

      toast({
        title: t('text661'),
        description: e instanceof Error ? e.message : undefined,
        variant: 'destructive',
      });
    }
  });

  const signIn = () => {
    close(AddReviewDialog.id);
    router.push(ROUTES.PROFILE());
  };

  const numbers = new Intl.NumberFormat(normalizeIntlLocale(locale));

  return (
    <Dialog.Portal>
      <DialogOverlay />
      <Dialog.Content
        // Focus the card itself (announces the title) instead of jumping to the first control.
        onOpenAutoFocus={event => {
          event.preventDefault();
          (event.currentTarget as HTMLElement).focus();
        }}
        className="fixed inset-x-0 bottom-0 z-[200] flex max-h-[calc(100dvh-24px)] flex-col rounded-t-[18px] bg-white shadow-[0_8px_24px_-4px_rgba(15,16,18,0.08),0_2px_4px_rgba(15,16,18,0.06)] focus:outline-none data-[state=open]:animate-in data-[state=open]:fade-in-0 motion-reduce:animate-none max-md:data-[state=open]:slide-in-from-bottom md:inset-x-auto md:bottom-auto md:left-1/2 md:top-1/2 md:max-h-[calc(100dvh-48px)] md:w-[calc(100%-32px)] md:max-w-[652px] md:-translate-x-1/2 md:-translate-y-1/2 md:rounded-[18px] md:data-[state=open]:zoom-in-95"
      >
        <div className="flex min-h-0 flex-col gap-6 overflow-y-auto overscroll-contain px-4 py-6 [scrollbar-color:#E2E4E7_transparent] [scrollbar-width:thin] md:p-6">
          <header className="flex items-start gap-6">
            <div className="flex min-w-0 flex-1 flex-col gap-2">
              <Dialog.Title className="dlz-h3 text-[#0F1012]">
                {REVIEW_COPY.form.title}
              </Dialog.Title>
              <Dialog.Description className="dlz-body-m text-[#6B7079]">
                {productName}
              </Dialog.Description>
            </div>
            <Dialog.Close
              aria-label={t('text272')}
              className="-m-2.5 shrink-0 rounded-full p-2.5 text-[#6B7079] transition-colors hover:text-[#0F1012] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0F1012]"
            >
              <Icon name="x" size={24} />
            </Dialog.Close>
          </header>

          {!me && isInitialized ? (
            <div className="flex flex-col gap-6">
              <p className="dlz-body-m text-[#4F545C]">{REVIEW_COPY.signIn.text}</p>
              <Button onClick={signIn} className="h-12 w-full md:h-[52px] md:w-auto md:self-start">
                {REVIEW_COPY.signIn.action}
              </Button>
            </div>
          ) : null}

          {me?.authToken ? (
            <Form {...form}>
              <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-6">
                <FormField
                  control={form.control}
                  name="rating"
                  render={({ field, fieldState }) => (
                    <FormItem required className="flex flex-col gap-2">
                      <FormLabel className="ml-0 text-[#0F1012]">
                        {REVIEW_COPY.form.rating}
                      </FormLabel>
                      <ReviewRatingInput
                        value={field.value}
                        onChange={field.onChange}
                        invalid={!!fieldState.error}
                        describedBy="add-review-rating-message"
                        label={REVIEW_COPY.form.rating}
                      />
                      <div id="add-review-rating-message">
                        {fieldState.error ? (
                          <FormMessage className="text-xs font-normal text-[#D7191F]" />
                        ) : (
                          <p className="dlz-caption text-[#6B7079]">
                            {REVIEW_COPY.form.ratingHint}
                          </p>
                        )}
                      </div>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="review"
                  render={({ field }) => (
                    <FormItem required className="flex flex-col gap-1">
                      <FormLabel className="ml-0 text-[#0F1012]">
                        {REVIEW_COPY.form.experience}
                      </FormLabel>
                      <FormControl>
                        <textarea
                          {...field}
                          rows={4}
                          maxLength={REVIEW_MAX_LENGTH}
                          placeholder={REVIEW_COPY.form.experiencePlaceholder}
                          className="dlz-body-m block w-full resize-none rounded-[18px] border border-[#E2E4E7] bg-white px-4 py-3 text-[#0F1012] transition-[border-color,box-shadow] placeholder:text-[#6B7079] hover:border-[#9096A0] focus:border-[#0F1012] focus:shadow-[inset_0_0_0_1px_#0F1012] focus:outline-none aria-[invalid=true]:border-[#D7191F] aria-[invalid=true]:shadow-[inset_0_0_0_1px_#D7191F]"
                        />
                      </FormControl>
                      <div className="flex items-start gap-4">
                        <FormMessage className="flex-1 text-xs font-normal text-[#D7191F]" />
                        <p className="dlz-caption ml-auto text-right text-[#6B7079]">
                          {numbers.format(field.value.length)} / {numbers.format(REVIEW_MAX_LENGTH)}
                        </p>
                      </div>
                    </FormItem>
                  )}
                />

                <div className="flex flex-col gap-6 md:flex-row md:items-center">
                  <p className="dlz-caption text-[#6B7079] md:flex-1">{REVIEW_COPY.form.note}</p>
                  <Button
                    type="submit"
                    isLoading={form.formState.isSubmitting}
                    className="h-12 w-full md:h-[52px] md:w-auto"
                  >
                    {REVIEW_COPY.form.submit}
                  </Button>
                </div>
              </form>
            </Form>
          ) : null}
        </div>
      </Dialog.Content>
    </Dialog.Portal>
  );
}

AddReviewDialog.id = 'ADD_REVIEW_DIALOG';
